/**
 * historySync — one-shot "Sync history to knowledge" (Phase 4, 2026-07-13).
 *
 * Chat history lives ONLY in this browser: Pi sessions in IndexedDB
 * (`vibe-pi-chat-<tenant>` via pi-web-ui AppStorage/SessionsStore) and voice
 * turns in localStorage (`vibe_voice_history_v2_<tenant>`). This module
 * collects both and POSTs them to the JWT-gated backend sync route, which
 * persists them server-side (content-hash dedup — re-sync is idempotent) and
 * ingests NEW snapshots into MIND `sessions`. Legacy-harness chat is
 * unpersisted by design and has nothing to sync.
 */
import { api } from '@/services/api';
import { getAppStorage } from '@earendil-works/pi-web-ui';
import { getStoredHistoryV2 } from '@/stores/voiceStore';

export interface HistoryTurnPayload {
  role: string;
  text: string;
}

export interface HistoryEntryPayload {
  harness: 'pi' | 'voice';
  session_id: string;
  title: string;
  turns: HistoryTurnPayload[];
}

export interface HistorySyncSummary {
  tenant_id: string;
  received: number;
  new: number;
  duplicates: number;
  skipped_empty: number;
  ingested: number;
  job_ids: string[];
  errors: string[];
}

/** Best-effort text from a pi-ai message content (string or block array). */
export function textFromMessageContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object' && 'text' in block) {
          const t = (block as { text?: unknown }).text;
          return typeof t === 'string' ? t : '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

/** Flatten Pi AgentMessages to {role, text} turns; empty turns dropped. */
export function turnsFromPiMessages(messages: unknown[]): HistoryTurnPayload[] {
  const turns: HistoryTurnPayload[] = [];
  for (const msg of messages) {
    if (!msg || typeof msg !== 'object') continue;
    const m = msg as { role?: unknown; content?: unknown };
    const text = textFromMessageContent(m.content).trim();
    if (!text) continue;
    turns.push({ role: typeof m.role === 'string' ? m.role : '?', text });
  }
  return turns;
}

/** Pi sessions from IndexedDB. Returns [] when Pi storage was never
 * initialized in this browser session (Pi panel not opened) — voice sync
 * still proceeds. */
export async function collectPiEntries(): Promise<HistoryEntryPayload[]> {
  let storage: ReturnType<typeof getAppStorage>;
  try {
    storage = getAppStorage();
  } catch {
    return [];
  }
  const entries: HistoryEntryPayload[] = [];
  const allMeta = await storage.sessions.getAllMetadata();
  for (const meta of allMeta) {
    const data = await storage.sessions.loadSession(meta.id);
    if (!data) continue;
    const turns = turnsFromPiMessages(data.messages as unknown[]);
    if (!turns.length) continue;
    entries.push({
      harness: 'pi',
      session_id: meta.id,
      title: data.title || meta.title || '',
      turns,
    });
  }
  return entries;
}

/** Voice turns from localStorage — one entry per voice tab. */
export function collectVoiceEntries(tenantId: string): HistoryEntryPayload[] {
  const byTab = getStoredHistoryV2(tenantId);
  const entries: HistoryEntryPayload[] = [];
  for (const [tabId, turns] of Object.entries(byTab)) {
    const payloadTurns = (turns ?? [])
      .map((t) => ({
        role: typeof t.role === 'string' ? t.role : '?',
        text: typeof t.text === 'string' ? t.text.trim() : '',
      }))
      .filter((t) => t.text);
    if (!payloadTurns.length) continue;
    entries.push({
      harness: 'voice',
      session_id: tabId,
      title: `Voice chat ${tabId}`,
      turns: payloadTurns,
    });
  }
  return entries;
}

/** Collect Pi + voice history and push it to the backend. Returns null when
 * this browser holds no history at all (nothing to sync — the backend
 * rejects an empty entries list by contract). */
export async function syncHistoryToKnowledge(
  tenantId: string,
): Promise<HistorySyncSummary | null> {
  const entries = [
    ...(await collectPiEntries()),
    ...collectVoiceEntries(tenantId),
  ];
  if (!entries.length) return null;
  return api.post<HistorySyncSummary>('/knowledge/history/sync', { entries });
}
